import React, { useState, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Search, PencilSquare, Trash, PlusCircle, Backspace, Building, FileText, Calendar3Event, Download, Copy,} from "react-bootstrap-icons"; // Iconos de Bootstrap
import { AlertError, AlertLoading } from "../helpers/AlertHelper";
import { formatDate, moment } from "../helpers/momentHelper";
import formatCurrency from "../helpers/currencyHelper";
import FormModal from "../components/modals/FormModal";
import TableManagementThreeButtons from "../components/tables/TableManagementThreeButtoms";
import SearchSelect from "../components/form/SearchSelect";
import ExpensesService from "../services/ExpensesService";
import ProvidersService from "../services/ProvidersService";
import ExpensesForm from "./ExpensesForm";
import { exportToExcel } from "../utils/exportToExcel";

const Expenses = () => {
  const intl = useIntl();
  const [expenseId, setExpenseId] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [currPage, setCurrPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [formattedData, setformattedData] = useState([]);
  const [providersList, setProvidersList] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [searchYear, setSearchYear] = useState("");

  // Setting modal title
  const [modalTitle, setModalTitle] = useState(false);

  // Setting modal icon
  const [modalIcon, setModalIcon] = useState(false);

  // Open modal form control to add/edit element
  const [modalTrigger, setModalTrigger] = useState(false);

  // Setting modal action enum[add,edit,delete]
  const [modalAction, setModalAction] = useState(false);

  // Fetch providers
  const fetchProviders = async () => {
    try {
      const response = await ProvidersService.getProviders(1, "", 2000);
      setProvidersList(response.data.providers);
    } catch (err) {
      setError(err);
    }
  };

  // Fetch general expenses
  const fetchExpenses = async () => {
    try {
      const response = await ExpensesService.getExpenses(
        currPage,
        search,
        perPage,
        sortBy,
        sortOrder,
        selectedProvider,
        searchYear
      );
      setExpenses(response.data.expenses);
      setTotalRows(response.data.pagination.totalItems);
      setIsLoaded(true);
    } catch (err) {
      setIsLoaded(false);
      setError(err);
    }
  };

  const handleModal = () => {
    setModalTrigger(false);
    fetchExpenses();
  };

  const handleSelectProviderChange = (e) => {
    setSelectedProvider(e.target.value);
    setCurrPage(1);
  };

  const handleYearSearchChange = (e) => {
    setSearchYear(e.target.value);
    setCurrPage(1); // Reinicia a la primera página cuando se busca
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrPage(1); // Reinicia a la primera página cuando se busca
  };

  const handleSearchClean = () => {
    setSearch("");
    setSelectedProvider("");
    setSearchYear("");
    setCurrPage(1); // Reinicia a la primera página cuando se busca
  };

  // fetch providers when start
  useEffect(() => {
    fetchProviders();
  }, []);

  // Re-fetch data on page/perPage change
  useEffect(() => {
    fetchExpenses();
  }, [
    currPage,
    perPage,
    search,
    sortBy,
    sortOrder,
    selectedProvider,
    searchYear,
  ]);

  const handleButtonClick = ({ data, type }) => {
    // Definir un objeto para mapear tipos a títulos e íconos
    const modalConfig = {
      add: {
        title: (
          <FormattedMessage id="expensesAdd" defaultMessage="Add expense" />
        ),
        icon: <PlusCircle />,
      },
      edit: {
        title: (
          <FormattedMessage id="expensesEdit" defaultMessage="Edit expense" />
        ),
        icon: <PencilSquare />,
      },
      delete: {
        title: (
          <FormattedMessage
            id="expensesDelete"
            defaultMessage="Delete expense"
          />
        ),
        icon: <Trash />,
      },
      duplicate: {
        title: <FormattedMessage id="textDuplicate" defaultMessage="Duplicate expense" />,
        icon: <Copy />,
      },
    };

    // Establecer la configuración del modal dependiendo del tipo
    const config = modalConfig[type] || {};

    if (config) {
      setExpenseId(type === "add" ? false : data._id); // Si 'add', expenseId será falso
      setModalTitle(config.title); // Set title from the config
      setModalIcon(config.icon); // Set icon from the config
      setModalAction(type);
      setModalTrigger(true);
    }
  };

  const handleRowsPerPageChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrPage(page);
  };

  const handleSort = async (column, sortDirection) => {
    setSortOrder(sortDirection);
    setSortBy(column.id);
    setCurrPage(1);
  };

  const columns = [
    {
      id: "providerId",
      name: (
        <FormattedMessage id="expensesProvider" defaultMessage="Provider" />
      ),
      selector: (row) => row.providerId.name,
      sortable: true,
    },
    {
      id: "expenseNumber",
      name: (
        <FormattedMessage id="expensesNumber" defaultMessage="Expense number" />
      ),
      selector: (row) => row.expenseNumber,
      sortable: true,
    },
    {
      id: "type",
      name: <FormattedMessage id="expensesType" defaultMessage="Type" />,
      selector: (row) => row.type,
      sortable: true,
    },
    {
      id: "concept",
      name: <FormattedMessage id="expensesConcept" defaultMessage="Concept" />,
      selector: (row) => row.concept,
    },
    {
      id: "date",
      name: (
        <FormattedMessage id="expensesDate" defaultMessage="Expense date" />
      ),
      selector: (row) => formatDate(row.date, "DD/MM/YYYY"),
      sortable: true,
    },
    {
      id: "paymentDate",
      name: (
        <FormattedMessage
          id="expensesPaymentDate"
          defaultMessage="Expense payment date"
        />
      ),
      selector: (row) => formatDate(row.paymentDate, "DD/MM/YYYY"),
      sortable: true,
    },
    {
      id: "amount",
      name: <FormattedMessage id="expensesAmount" defaultMessage="Amount" />,
      selector: (row) => formatCurrency(row.amount),
      sortable: true,
      right: "true",
    },
  ];

  const headersMap = {
    projectId: "Proyecto",
    providerId: "Proveedor",
    expenseNumber: "Número de gasto",
    type: "Tipo",
    concept: "Concepto",
    date: "Fecha",
    paymentDate: "Fecha de pago",
    amount: "Importe",
  };

  useEffect(() => {
    let formattedExpensesProjects = expenses.map((item) => ({
      [headersMap.projectId]: item.projectId?.name || "",
      [headersMap.providerId]: item.providerId?.name || "",
      [headersMap.expenseNumber]: item.expenseNumber || "",
      [headersMap.type]: item.type || "",
      [headersMap.concept]: item.concept || "",
      [headersMap.date]: item.date
        ? moment(item.date).format("DD/MM/YYYY")
        : "",
      [headersMap.paymentDate]: item.paymentDate
        ? moment(item.paymentDate).format("DD/MM/YYYY")
        : "",
      [headersMap.amount]: formatCurrency(item.amount || 0),
    }));

    setformattedData(formattedExpensesProjects);
  }, [expenses]);

  return (
    <Container fluid className="p-0">
      <Row className="p-1 bg-secondary">
        <Col>
          <InputGroup>
            <InputGroup.Text>
              <Building />
            </InputGroup.Text>
            <SearchSelect
              fieldName={"providerId"}
              selected={selectedProvider}
              onChange={handleSelectProviderChange}
              options={providersList}
            />
            <InputGroup.Text>
              <Calendar3Event />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={intl.formatMessage({
                id: "expensesTextSearchYear",
                defaultMessage: "Search by year...",
              })}
              value={searchYear}
              onChange={handleYearSearchChange}
            />
            <InputGroup.Text>
              <Search />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={intl.formatMessage({
                id: "textSearch",
                defaultMessage: "Search expenses...",
              })}
              value={search}
              onChange={handleSearchChange}
            />
            <Button onClick={handleSearchClean} variant="light">
              <Backspace />{" "}
              <FormattedMessage id="textClean" defaultMessage="Clean" />
            </Button>

            <Button
              onClick={() => handleButtonClick({ data: null, type: "add" })}
              variant="primary"
            >
              <PlusCircle />{" "}
              <FormattedMessage id="textAdd" defaultMessage="Add" />
            </Button>
            {formattedData.length > 0 && (
              <Button
                onClick={() =>
                  exportToExcel(formattedData, "gastos.xlsx", "Tipo", "Importe")
                }
                variant="success"
              >
                <Download />{" "}
                <FormattedMessage id="downloadExcel" defaultMessage="Export" />
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>
      {isLoaded ? (
        <TableManagementThreeButtons
          icon={<FileText />}
          title={
            <FormattedMessage
              id="navExpensesGeneral"
              defaultMessage="General Expenses"
            />
          }
          columns={columns}
          data={expenses}
          paginationPerPage={perPage}
          paginationTotalRows={totalRows}
          onChangePage={setCurrPage}
          onChangeRowsPerPage={handleRowsPerPageChange}
          onSort={handleSort}
          onActionClick={handleButtonClick}
        />
      ) : error ? (
        <AlertError error={error} />
      ) : (
        <AlertLoading isLoaded={isLoaded} />
      )}

      <FormModal
        title={modalTitle}
        icon={modalIcon}
        body={ExpensesForm}
        refreshData={handleModal}
        modalTrigger={modalTrigger}
        elementId={expenseId}
        action={modalAction}
      />
    </Container>
  );
};

export default Expenses;
